import React from 'react';
import { Link } from 'react-router-dom';

import AuthGuard from '../Auth/AuthGuard';
import AppNav from '../AppNav/AppNav';

import './Instructions.css';

const steps = [
  {
    number: 1,
    title: 'Find an area',
    description: 'Enter an address, town, county, ZIP code, or state in the search box. Choose a suggestion to move the map to that location.'
  },
  {
    number: 2,
    title: 'Zoom in to load wells',
    description: 'Use the + and − controls, your mouse wheel, or pinch-to-zoom. Well records appear once the map is close enough.'
  },
  {
    number: 3,
    title: 'Select a well group',
    description: 'Blue circles mark wells or groups of nearby wells. Larger circles contain more records. Select a circle to highlight it in red and view its records.'
  },
  {
    number: 4,
    title: 'Open a well report',
    description: 'The results panel shows the driller or address, state ID, drilled date, and depth. Select a blue record title to open the scanned state report in a new tab.'
  }
];

const Instructions = () => (
  <AuthGuard>
    <AppNav>
      <main className="InstructionsPage">
        <section className="InstructionsHero">
          <div className="InstructionsHero-content">
            <p className="InstructionsEyebrow">Allied Water Well Log</p>
            <h1>How to use the well map</h1>
            <p className="InstructionsLead">
              Search more than one million public well records across Maryland, New York, and Pennsylvania.
            </p>
            <Link className="btn btn-primary InstructionsMapButton" to="/">
              Open the map
            </Link>
          </div>
          <div className="InstructionsMarkerExample" aria-label="Example selected well marker">
            <span className="InstructionsMarker InstructionsMarker-blue" />
            <span className="InstructionsMarker InstructionsMarker-red" />
          </div>
        </section>

        <section className="InstructionsSection" aria-labelledby="getting-started-heading">
          <h2 id="getting-started-heading">Getting started</h2>
          <div className="InstructionsSteps">
            {steps.map(step => (
              <article className="InstructionsStep" key={step.number}>
                <div className="InstructionsStep-number" aria-hidden="true">{step.number}</div>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.description}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="InstructionsSection InstructionsDetails" aria-labelledby="reading-results-heading">
          <div className="InstructionsPanel">
            <h2 id="reading-results-heading">Reading the results</h2>
            <ul>
              <li><strong>Circle size:</strong> a larger circle represents more records at or near that point.</li>
              <li><strong>Red circle:</strong> the currently selected group.</li>
              <li><strong>Blue record title:</strong> a scanned well report is available; it opens in a new tab.</li>
              <li><strong>Black record title:</strong> the record is mapped, but a scanned report is not currently available.</li>
              <li><strong>Depth bar:</strong> compares that well's depth with the other records in the selected group.</li>
            </ul>
          </div>

          <div className="InstructionsPanel">
            <h2>Map controls</h2>
            <ul>
              <li>Drag the map to explore nearby areas. Results refresh for the visible map area.</li>
              <li>Use <strong>Default</strong>, <strong>Satellite</strong>, or <strong>Streets</strong> below the search box to change the background map.</li>
              <li>If there are too many records to display, zoom in or search for a more specific location.</li>
              <li>Use the state ID when referencing a record with a state agency or customer.</li>
            </ul>
          </div>
        </section>

        <section className="InstructionsNotice" aria-labelledby="data-note-heading">
          <div>
            <h2 id="data-note-heading">About the data</h2>
            <p>
              Locations and report details come from public state records and may be incomplete or approximate.
              The map is a research aid; confirm critical information with the original report or the appropriate state agency.
            </p>
          </div>
          <a
            className="InstructionsDataLink"
            href="https://docs.google.com/spreadsheets/d/1f0g5JER94jZ56diUzyaZtTytrormYNlBiw32156_WCw/edit?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
          >
            View map data availability <span aria-hidden="true">↗</span>
          </a>
        </section>
      </main>
    </AppNav>
  </AuthGuard>
);

export default Instructions;
